import { safeParseJson } from './storage-utils';

export type EIAssessmentAnswer = {
  questionId: string;
  value: number; // 1-5 Likert
};

export type EIAssessmentDomainKey =
  | 'selfAwareness'
  | 'selfRegulation'
  | 'empathy'
  | 'communication'
  | 'repair';

export type EIAssessmentResult = {
  completedAt: string;
  overallScore: number; // 0-100
  domainScores: Record<EIAssessmentDomainKey, number>;
  path: Array<string>;
};

export type EIAssessmentQuestion = {
  id: string;
  prompt: string;
  domain: EIAssessmentDomainKey;
};

/** Plain-language labels for each skill area (shown in the Relationship Intelligence assessment UI). */
export const EI_DOMAIN_META: Record<
  EIAssessmentDomainKey,
  { title: string; tagline: string; color: string }
> = {
  selfAwareness: {
    title: 'Knowing yourself',
    tagline: 'Noticing feelings and early signals',
    color: 'violet',
  },
  selfRegulation: {
    title: 'Staying steady',
    tagline: 'Pausing and cooling down under stress',
    color: 'sky',
  },
  empathy: {
    title: 'Seeing their side',
    tagline: 'Curiosity and perspective-taking',
    color: 'rose',
  },
  communication: {
    title: 'Saying it clearly',
    tagline: 'Requests, validation, and clarity',
    color: 'amber',
  },
  repair: {
    title: 'Bouncing back',
    tagline: 'Repair and reconnecting after tension',
    color: 'emerald',
  },
};

export const EI_ASSESSMENT_STORAGE_KEY = 'tension_ei_assessment_snapshot_v1';

export const EI_QUESTIONS: Array<EIAssessmentQuestion> = [
  {
    id: 'sa_1',
    domain: 'selfAwareness',
    prompt: 'Most of the time, I can tell what I’m feeling before I react.',
  },
  {
    id: 'sa_2',
    domain: 'selfAwareness',
    prompt: 'I notice early when something is starting to get under my skin.',
  },
  {
    id: 'sr_1',
    domain: 'selfRegulation',
    prompt: 'I can hit pause before sending a heated message.',
  },
  {
    id: 'sr_2',
    domain: 'selfRegulation',
    prompt: 'After a spike, I can calm back down without staying stuck all day.',
  },
  {
    id: 'em_1',
    domain: 'empathy',
    prompt: 'I can repeat back my partner’s view in a way they’d agree with.',
  },
  {
    id: 'em_2',
    domain: 'empathy',
    prompt: 'Even when we disagree, I stay curious instead of shutting down.',
  },
  {
    id: 'cm_1',
    domain: 'communication',
    prompt: 'When I ask for something, I’m specific enough that they know what to do.',
  },
  {
    id: 'cm_2',
    domain: 'communication',
    prompt: 'I acknowledge how they feel before I jump to fixing the problem.',
  },
  {
    id: 'rp_1',
    domain: 'repair',
    prompt: 'After a fight or cold moment, I’m willing to reach out and make it right.',
  },
  {
    id: 'rp_2',
    domain: 'repair',
    prompt: 'We can feel close again after things get rocky—not just “move on” in silence.',
  },
];

function clampScore(input: number): number {
  return Math.max(0, Math.min(100, Math.round(input)));
}

export function scoreEIAssessment(answers: Array<EIAssessmentAnswer>): EIAssessmentResult {
  const grouped: Record<EIAssessmentDomainKey, Array<number>> = {
    selfAwareness: [],
    selfRegulation: [],
    empathy: [],
    communication: [],
    repair: [],
  };

  for (const answer of answers) {
    const question = EI_QUESTIONS.find((q) => q.id === answer.questionId);
    if (!question) continue;
    grouped[question.domain].push(Math.max(1, Math.min(5, answer.value)));
  }

  const domainScores = Object.fromEntries(
    Object.entries(grouped).map(([domain, values]) => {
      if (values.length === 0) return [domain, 0];
      const avg = values.reduce((sum, value) => sum + value, 0) / values.length;
      return [domain, clampScore(((avg - 1) / 4) * 100)];
    }),
  ) as Record<EIAssessmentDomainKey, number>;

  const overallScore = clampScore(
    Object.values(domainScores).reduce((sum, value) => sum + value, 0) / Object.values(domainScores).length,
  );

  const path = buildLearningPath(domainScores);

  return {
    completedAt: new Date().toISOString(),
    overallScore,
    domainScores,
    path,
  };
}

export function buildLearningPath(domainScores: Record<EIAssessmentDomainKey, number>): Array<string> {
  const byNeed = Object.entries(domainScores).sort((a, b) => a[1] - b[1]);
  return byNeed.slice(0, 3).map(([domain]) => {
    switch (domain as EIAssessmentDomainKey) {
      case 'selfAwareness':
        return 'Name one feeling + one trigger out loud before a hard talk.';
      case 'selfRegulation':
        return 'Wait 90 seconds before sending a text when you feel heated.';
      case 'empathy':
        return 'Try one “What I hear you saying is…” reflection in your next chat.';
      case 'communication':
        return 'Swap one critique for one feeling plus one small, clear ask.';
      case 'repair':
        return 'Within a day after a fight, send one short repair message or hug-it-out moment.';
      default:
        return 'Keep one small EI habit on repeat this week.';
    }
  });
}

export function saveEIAssessmentSnapshot(result: EIAssessmentResult): void {
  localStorage.setItem(EI_ASSESSMENT_STORAGE_KEY, JSON.stringify(result));
}

export function getLatestEIAssessmentSnapshot(): EIAssessmentResult | null {
  return safeParseJson<EIAssessmentResult | null>(
    localStorage.getItem(EI_ASSESSMENT_STORAGE_KEY),
    null,
  );
}
